import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Callout } from 'components/layout/Callout';
import { DateRangeFilterLayer } from './DateRangeFilterLayer';
import './DateRangeFilter.less';
import classNames from 'classnames';
import {
  newReportDateRange,
  ReportDateRange,
} from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/ReportDateRange';
import { DateRangeTypes } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/DateRangeTypes';
import { RollingDateRangeType } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/RollingDateRangeType';
import { RollingDateRangeTimePeriodType } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/RollingDateRangeTimePeriodType';
import { getMoment, isMinDate } from 'utils/DateTimeUtils';

const moment = getMoment();

export interface IDateRangeFilterProps {
  wrapperClass?: string;
  buttonClass?: string;
  dateRangeFilterData: ReportDateRange;
  disableRollingRange?: boolean;
  showInLayer: boolean;
  onSelectionChange: (range: ReportDateRange) => void;
}

function validateDateRangeFilterData(dateRangeFilterData?: ReportDateRange): boolean {
  if (dateRangeFilterData?.DateRange === DateRangeTypes.Custom) {
    return dateRangeFilterData.NumberOfDays > 0;
  } else if (dateRangeFilterData?.DateRange === DateRangeTypes.Rolling) {
    return (
      dateRangeFilterData.RollingDateRangeTimePeriodType &&
      dateRangeFilterData.RollingDateRangeType &&
      dateRangeFilterData.RollingPeriod > 0
    );
  }

  return dateRangeFilterData?.DateRange !== undefined && dateRangeFilterData.DateRange !== DateRangeTypes.Other;
}

export function DateRangeFilter({
  dateRangeFilterData: initialDateRangeFilterData,
  wrapperClass,
  buttonClass,
  showInLayer,
  disableRollingRange,
  onSelectionChange: onSelectionChangeHandler,
}: IDateRangeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [label, setLabel] = useState(getDateRangeFilterLabel(initialDateRangeFilterData));
  const [dateRangeFilterData, setDateRangeFilterData] = useState(initialDateRangeFilterData);
  const [selectedDateRangeFilterData, setSelectedDateRangeFilterData] = useState(dateRangeFilterData);
  const [isValid, setIsValid] = useState(true);

  const ref: React.MutableRefObject<any> = useRef(null);

  useEffect(() => {
    const filterData = initialDateRangeFilterData ?? { ...newReportDateRange(), DateRange: DateRangeTypes.None };
    // if date is min date, if yes set it to current date as min date of javascript throws error on server
    if (isMinDate(moment.utc(filterData.FromDate))) {
      filterData.FromDate = moment().toDate();
    }
    setDateRangeFilterData(filterData);
    setLabel(getDateRangeFilterLabel(filterData));
    setSelectedDateRangeFilterData(filterData);
    setIsValid(validateDateRangeFilterData(filterData));
  }, [initialDateRangeFilterData]);

  const raiseChangeEvent = useCallback(
    (dateRange: ReportDateRange) => {
      setIsOpen(false);
      const valid = validateDateRangeFilterData(dateRange);
      if (valid) {
        setDateRangeFilterData(dateRange);
        onSelectionChangeHandler(dateRange);
      }
      setIsValid(valid);
    },
    [onSelectionChangeHandler]
  );

  const onClose = useCallback(() => {
    setIsOpen(false);
    setLabel(getDateRangeFilterLabel(dateRangeFilterData));
    setSelectedDateRangeFilterData(dateRangeFilterData);
    setIsValid(validateDateRangeFilterData(dateRangeFilterData));
  }, [dateRangeFilterData]);

  const onSelectionChange = useCallback(
    (dateRangeFilterData: ReportDateRange) => {
      if (showInLayer) {
        setSelectedDateRangeFilterData(dateRangeFilterData);
        setLabel(getDateRangeFilterLabel(dateRangeFilterData));
        setIsValid(validateDateRangeFilterData(dateRangeFilterData));
      } else {
        raiseChangeEvent(dateRangeFilterData);
      }
    },
    [raiseChangeEvent, showInLayer]
  );

  const onClick = useCallback(() => {
    raiseChangeEvent(selectedDateRangeFilterData);
  }, [raiseChangeEvent, selectedDateRangeFilterData]);

  const onClearClick = useCallback(() => {
    const filterData = {
      ...dateRangeFilterData,
      DateRange: DateRangeTypes.None,
    };
    setLabel(getDateRangeFilterLabel(filterData));
    raiseChangeEvent(filterData);
  }, [dateRangeFilterData, raiseChangeEvent]);

  const clearButton = useMemo(() => {
    return (
      <button
        className={classNames('dp-button', 'dpc-date-range-filter__button', {
          'ui-button-disabled': dateRangeFilterData?.DateRange === DateRangeTypes.None,
        })}
        onClick={onClearClick}
        disabled={dateRangeFilterData?.DateRange === DateRangeTypes.None}
      >
        Clear
      </button>
    );
  }, [dateRangeFilterData?.DateRange, onClearClick]);

  const dateFilter = useMemo(() => {
    return (
      <>
        <DateRangeFilterLayer
          disableRollingRange={disableRollingRange}
          dateRangeFilterData={dateRangeFilterData}
          onSelectionChange={onSelectionChange}
        />
        <div className="dpc-date-range-filter__footer">
          {clearButton}
          {showInLayer && (
            <button
              className={classNames(
                'dp-button',
                'dpc-date-range-filter__button',
                'dpc-date-range-filter__button--apply',
                {
                  'ui-button-disabled': !isValid,
                }
              )}
              onClick={onClick}
              disabled={!isValid}
            >
              Apply
            </button>
          )}
        </div>
      </>
    );
  }, [clearButton, dateRangeFilterData, disableRollingRange, isValid, onClick, onSelectionChange, showInLayer]);

  return (
    <div className={classNames('dpc-date-range-filter', wrapperClass)}>
      {showInLayer && (
        <button
          className={classNames('dpc-date-range-filter__open-btn', buttonClass)}
          ref={ref}
          onClick={() => setIsOpen(true)}
        >
          {label}
        </button>
      )}
      {showInLayer ? (
        <Callout isOpen={isOpen} sourceRef={ref} onClose={onClose} showArrow={true}>
          {dateFilter}
        </Callout>
      ) : (
        dateFilter
      )}
    </div>
  );
}

export function getDateRangeFilterLabel(dateRangeFilterData?: ReportDateRange): string {
  switch (dateRangeFilterData?.DateRange) {
    case DateRangeTypes.None:
      return 'None';
    case DateRangeTypes.Today:
      return 'Today';
    case DateRangeTypes.LastWeek:
      return 'Last Week';
    case DateRangeTypes.LastMonth:
      return 'Last Month';
    case DateRangeTypes.LastQuarter:
      return 'Last Quarter';
    case DateRangeTypes.LastYear:
      return 'Last Year';
    case DateRangeTypes.ThisWeek:
      return 'This Week';
    case DateRangeTypes.ThisMonth:
      return 'This Month';
    case DateRangeTypes.ThisQuarter:
      return 'This Quarter';
    case DateRangeTypes.ThisYear:
      return 'This Year';
    case DateRangeTypes.NextWeek:
      return 'Next Week';
    case DateRangeTypes.NextMonth:
      return 'Next Month';
    case DateRangeTypes.NextQuarter:
      return 'Next Quarter';
    case DateRangeTypes.NextYear:
      return 'Next Year';
    case DateRangeTypes.Custom:
      return 'Custom Date Range';
    case DateRangeTypes.Rolling:
      return validateDateRangeFilterData(dateRangeFilterData)
        ? `${RollingDateRangeType[dateRangeFilterData.RollingDateRangeType]} ${dateRangeFilterData.RollingPeriod} ${
            RollingDateRangeTimePeriodType[dateRangeFilterData.RollingDateRangeTimePeriodType]
          }`
        : 'Rolling Date Range';
  }

  return 'Date Range Filter';
}
